import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';
import PremiumStar from '@/components/Icons/PremiumStar';
import { PLACEHOLDER_IMG_URL } from '@/constants/images';
import { logSearch } from '@/features/GlobalSearch/api/logging';
import { parseSearchResults } from '@/features/GlobalSearch/utils/parseSearchResult';
import { parseTrendingResults } from '@/features/GlobalSearch/utils/parseTrendingResult';
import { useTracking } from '@/providers/TrackingProvider';
import type { FormValues } from '@/features/GlobalSearch/globalSearchForm';
import type { TrendingResponse } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import { useMemo } from 'react';
interface SuggestedTrendingProps {
  item: TrendingResponse;
  type: 'trending';
}
interface SuggestedSearchProps {
  item: GenericSearchResult;
  type: 'search';
}
type SuggestedItemProps = {
  closePopup: () => void;
  searchTerm?: string;
  searchType?: 'TRENDING' | 'SUGGESTED';
} & (SuggestedTrendingProps | SuggestedSearchProps);
export const SuggestedItem: React.FC<SuggestedItemProps> = ({
  item,
  type,
  closePopup,
  searchTerm,
  searchType
}) => {
  const {
    initializeTracking
  } = useTracking();
  const router = useRouter();
  const {
    getValues
  } = useFormContext<FormValues>();
  const parsedItem = type === 'trending' ? parseTrendingResults(item) : parseSearchResults(item);
  const imageUrl = useMemo(() => {
    if (!parsedItem) {
      return PLACEHOLDER_IMG_URL;
    }
    if (parsedItem.contributor_image) {
      return parsedItem.contributor_image;
    }
    return parsedItem.image != null && parsedItem.image !== '' ? parsedItem?.image : PLACEHOLDER_IMG_URL;
  }, [parsedItem]);
  const contributor = useMemo(() => {
    if (!parsedItem) {
      return null;
    }
    if (!(parsedItem.artist || parsedItem.contributor_name)) {
      return null;
    }
    return {
      name: parsedItem.artist || parsedItem.contributor_name,
      slug: parsedItem.artist_slug || parsedItem.contributor_slug
    };
  }, [parsedItem]);
  if (!parsedItem) {
    return null;
  }
  const isSfx = parsedItem.type?.toLowerCase() === 'sfx';
  const slug = isSfx && parsedItem.slug?.startsWith('/sfx/') ? parsedItem.slug : isSfx ? `/sfx/${parsedItem.slug}` : `/music/tracks/${parsedItem.contributor_slug}/${parsedItem.track_slug}`;
  return <div className="main-search-result group relative -mx-2 -my-1 flex items-center gap-3 rounded-2xl p-2 align-middle transition-all duration-300 md:hover:bg-gray-50 dark:bg-gray-875 dark:md:hover:bg-gray-900" data-sentry-component="SuggestedItem" data-sentry-source-file="SuggestedItem.tsx">
      <Link href={slug} className="absolute inset-0 top-0 left-0 z-1 h-full w-full" onClick={() => {
      closePopup();
      void initializeTracking({
        endSlug: slug,
        content: parsedItem.name,
        access: 'Search',
        assetType: isSfx ? 'sfx' : 'music'
      });
      const assetType = getValues('assetType');
      if (searchType) {
        void logSearch(searchTerm ?? '', router.asPath, slug, searchType, assetType);
      }
    }} data-sentry-element="Link" data-sentry-source-file="SuggestedItem.tsx" />
      <div className="main-search-result-image pointer-events-none relative rounded-full">
        <Image key={parsedItem?.image} className={clsx('main-search__thumb rounded-full', parsedItem.isTrackItem ? 'round-img' : '', 'h-[56px] w-[56px] object-cover 2xl:h-[64px] 2xl:w-[64px]')} src={imageUrl} width={64} height={64} quality={64} alt={parsedItem.name ?? ''} data-sentry-element="Image" data-sentry-source-file="SuggestedItem.tsx" />
        {type === 'search' && !!item.is_premium && <div className="absolute top-0 left-0 flex h-4 w-4 items-center justify-center rounded-full border border-gray-50 border-solid dark:border-gray-900">
            <PremiumStar className="size-5" />
          </div>}
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-between">
        <div className="main-search-result-left flex min-w-0 flex-col">
          <span className="list-title-name truncate font-semibold text-gray-950 dark:text-white">
            {parsedItem.name}
          </span>
          {!!contributor && !!contributor.slug && contributor.slug.trim() !== '' && <div>
                <Link href={`/contributors/${contributor.slug}`} onClick={() => {
            closePopup();
            const assetType = getValues('assetType');
            if (searchType) {
              void logSearch(searchTerm ?? '', router.asPath, `/contributors/${contributor.slug}`, searchType, assetType);
            }
          }} className={isSfx && !contributor.name ? 'inline-block shrink rounded-lg bg-transparent text-gray-950 text-xs transition-all duration-300 dark:text-white' : 'relative z-2 inline-block shrink rounded-lg bg-transparent text-gray-950 text-xs transition-all duration-300 dark:text-white'}>
                  {contributor.name}
                </Link>
              </div>}
          {!!contributor && !contributor.slug && <div>
              <span className={isSfx && !contributor.name ? 'inline-block shrink rounded-lg bg-transparent text-gray-950 text-xs transition-all duration-300 dark:text-white' : 'pointer-events-none relative z-2 inline-block shrink rounded-lg bg-transparent text-gray-950 text-xs transition-all duration-300 dark:text-white'}>
                {contributor.name}
              </span>
            </div>}
        </div>
        <div className="main-search-result-tags flex gap-2">
          {parsedItem.featured_tags?.map((tag, index) => <div key={tag.url && tag.url !== '' ? tag.url : tag.name} className={index >= 2 ? 'hidden md:block' : ''}>
              {tag.url && tag.url !== '' ? <Link className="relative bottom-0 z-2 inline-block shrink whitespace-nowrap rounded-xl bg-gray-100 px-2 py-1 text-gray-950 text-xs transition-all duration-300 hover:bg-gray-200! dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700!" href={tag.url} onClick={() => {
            closePopup();
            const assetType = getValues('assetType');
            if (searchType) {
              void logSearch(searchTerm ?? '', router.asPath, tag.url ?? '', searchType, assetType);
            }
          }}>
                  {tag.name.toLowerCase()}
                </Link> : <span className="relative bottom-0 z-2 inline-block shrink whitespace-nowrap rounded-xl bg-gray-100 px-2 py-1 text-gray-950 text-xs transition-all duration-300 hover:bg-gray-50! group-hover:bg-white dark:bg-gray-900 dark:text-white group-hover:dark:bg-gray-900 dark:hover:bg-gray-900!">
                  {tag.name.toLowerCase()}
                </span>}
            </div>)}
        </div>
      </div>
    </div>;
};
SuggestedItem.displayName = 'SuggestedItem';